import { useCallback, useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { cleanTrip, getGuideTrips, getGuideTripsSearch } from "store/trips"

const MIN_SEARCH_CHARS = 3
// Sort is passed to the API as two separate params (sort_by + order), matching
// the bookings endpoint — not a combined "field_direction" string.
const DEFAULT_SORT = { sort_by: "updated_at", order: "desc" }

export const useExperienceListingsFetch = ({
  activeListingTab,
  page,
  pageSize,
  metadata,
  searchQuery,
  confirmedSearchQuery,
  debouncedSearchQuery,
  selectedTrip,
  buildFilterQuery,
  sortBy: sortByProp
}) => {
  const sort = sortByProp || DEFAULT_SORT
  const dispatch = useDispatch()
  const [originalStatusCount, setOriginalStatusCount] = useState(null)

  // Preserve original status_count before search API overwrites it
  useEffect(() => {
    if (metadata?.status_count && !searchQuery.trim()) {
      setOriginalStatusCount(metadata.status_count)
    }
  }, [metadata?.status_count, searchQuery])

  // Fetch by tab + pagination
  useEffect(() => {
    if (!activeListingTab) return undefined
    if (confirmedSearchQuery.trim() || selectedTrip) return undefined

    const statusFilter = activeListingTab === "all" ? {} : { status_eq: activeListingTab }
    dispatch(
      getGuideTrips(null, {
        q: { ...statusFilter, ...buildFilterQuery() },
        page,
        per_page: pageSize,
        sort_by: sort.sort_by,
        order: sort.order
      })
    )

    return () => {
      dispatch(cleanTrip())
    }
  }, [dispatch, activeListingTab, page, pageSize, confirmedSearchQuery, selectedTrip, buildFilterQuery, sort.sort_by, sort.order])

  // Fetch suggestions while typing
  useEffect(() => {
    if (confirmedSearchQuery.trim()) return
    if (debouncedSearchQuery.trim().length < MIN_SEARCH_CHARS || selectedTrip) return

    dispatch(
      getGuideTripsSearch(null, {
        q: { title_cont: debouncedSearchQuery.trim(), ...buildFilterQuery() },
        page,
        per_page: pageSize,
        sort_by: sort.sort_by,
        order: sort.order,
        search: true
      })
    )
  }, [debouncedSearchQuery, dispatch, page, pageSize, confirmedSearchQuery, selectedTrip, buildFilterQuery, sort.sort_by, sort.order])

  // Fetch paginated search results after Enter / select
  useEffect(() => {
    if (!confirmedSearchQuery.trim() || selectedTrip) return

    dispatch(
      getGuideTripsSearch(null, {
        q: { title_cont: confirmedSearchQuery.trim(), ...buildFilterQuery() },
        page,
        per_page: pageSize,
        sort_by: sort.sort_by,
        order: sort.order,
        search: false
      })
    )
  }, [confirmedSearchQuery, page, pageSize, dispatch, selectedTrip, buildFilterQuery, sort.sort_by, sort.order])

  const refetchCurrent = useCallback(() => {
    const statusFilter = activeListingTab === "all" ? {} : { status_eq: activeListingTab }
    dispatch(
      getGuideTrips(null, {
        q: { ...statusFilter, ...buildFilterQuery() },
        page,
        per_page: pageSize,
        sort_by: sort.sort_by,
        order: sort.order
      })
    )
  }, [dispatch, activeListingTab, page, pageSize, buildFilterQuery, sort.sort_by, sort.order])

  const fetchTripByTitle = useCallback(
    (trip) => {
      dispatch(
        getGuideTripsSearch(null, {
          q: { status_eq: trip.status, title_eq: trip.title },
          page: 1,
          per_page: pageSize,
          sort_by: sort.sort_by,
          order: sort.order,
          search: false
        })
      )
    },
    [dispatch, pageSize, sort.sort_by, sort.order]
  )

  return { originalStatusCount, refetchCurrent, fetchTripByTitle }
}
